"use client"
import * as Avatar from '@radix-ui/react-avatar';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { LogOut, Menu, Settings, User, X } from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import NotificationDropdown from './NotificationDropdown';

interface AdminHeaderProps {
  onMenuToggle: () => void;
  isMobileSidebarOpen: boolean;
  isDesktopView: boolean;
  isSidebarCollapsed: boolean;
}

// Navigation structure to match AdminSidebar
const navigationStructure = [
  { name: 'Dashboard', href: '/admin' },
  {
    name: 'Content',
    children: [
      { name: 'Blog Management', href: '/admin/blogs' },
      { name: 'News-PR Management', href: '/admin/news-pr' },
      { name: 'Thought & Leadership Management', href: '/admin/thought-leadership' },
      { name: 'FAQ Management', href: '/admin/faqs' },
      { name: 'Categories', href: '/admin/categories' },
      { name: 'Testimonial Management', href: '/admin/testimonials' },
    ]
  },
  { name: 'Users', href: '/admin/users' },
  {
    name: 'Account Management',
    children: [
      { name: 'Account Info', href: '/admin/account' },
      { name: 'Task Management', href: '/admin/account/tasks' }
    ]
  },
  {
    name: 'Forms & Submissions',
    children: [
      { name: 'Contacts Form', href: '/admin/contacts' },
      { name: 'Blog Subscription', href: '/admin/subscriptions' }
    ]
  },
  { name: 'Media', href: '/admin/media' },
  { name: 'Social Media Management', href: '/admin/social-media-management' },
  { name: 'Trash', href: '/admin/trash' },
  {
    name: 'Website-Management',
    children: [
      { name: 'Redirections', href: '/admin/redirects' },
      { name: '410 Management', href: '/admin/410-management' },
      { name: 'Interlinking-Management', href: '/admin/interlinking' },
      { name: 'Service Pages', href: '/admin/service-meta' },
      { name: 'Post Ownership Management', href: '/admin/post-ownership' },
    ]
  },
  {
    name: 'System',
    children: [
      { name: 'Audit Logs', href: '/admin/logs' },
      { name: 'Backup & Restore', href: '/admin/backup-restore' },
    ]
  },
];

function getActiveTabName(pathname: string): { mainTab: string; subTab: string | null } {
  // Check for exact dashboard match
  if (pathname === '/admin') {
    return { mainTab: 'Dashboard', subTab: null };
  }

  // Check child items first (more specific)
  for (const item of navigationStructure) {
    if (item.children) {
      for (const child of item.children) {
        if (child.href === '/admin') {
          if (pathname === child.href) {
            return { mainTab: item.name, subTab: child.name };
          }
        } else if (pathname.startsWith(child.href)) {
          return { mainTab: item.name, subTab: child.name };
        }
      }
    }
  }

  // Check direct links
  for (const item of navigationStructure) {
    if (item.href) {
      if (item.href === '/admin') {
        if (pathname === item.href) {
          return { mainTab: item.name, subTab: null };
        }
      } else if (pathname.startsWith(item.href)) {
        return { mainTab: item.name, subTab: null };
      }
    }
  }

  // Default fallback
  return { mainTab: 'Make My Brand', subTab: null };
}

export default function AdminHeader({
  onMenuToggle,
  isMobileSidebarOpen,
  isDesktopView,
  isSidebarCollapsed
}: AdminHeaderProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const { mainTab, subTab } = getActiveTabName(pathname);

  const handleLogout = async () => {
    try {
      // Clear refresh token from database
      const refreshToken = (session as unknown as { refreshToken?: string })?.refreshToken
      if (refreshToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });
      }

      // Clear local storage and cookies
      if (typeof window !== 'undefined') {
        localStorage.clear();
        sessionStorage.clear();

        // Clear NextAuth cookies
        document.cookie.split(';').forEach(c => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
        });
      }

      // Sign out from NextAuth without forcing a server-side redirect
      // Then navigate to the admin login page on the current origin
      await signOut({ redirect: false });
      router.push('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect even if logout fails
      router.push('/admin/login');
    }
  };

  return (
    <header className="bg-transparent backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 sm:space-x-4">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuToggle}
            className="p-1.5 rounded-lg bg-blue-50/50 backdrop-blur-md border border-blue-100/50 hover:bg-blue-100/60 hover:border-blue-200/60 transition-all duration-200"
            aria-label={
              !isDesktopView && isMobileSidebarOpen
                ? 'Close sidebar'
                : isSidebarCollapsed
                  ? 'Expand sidebar'
                  : 'Collapse sidebar'
            }
          >
            {!isDesktopView && isMobileSidebarOpen ? (
              <X className="h-5 w-5 text-blue-400 hover:text-blue-500 transition-colors" />
            ) : (
              <Menu className="h-5 w-5 text-blue-400 hover:text-blue-500 transition-colors" />
            )}
          </button>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <h1 className="text-lg   text-gray-800 truncate">
              {subTab || mainTab}
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Notifications */}
          <div className="backdrop-blur-sm">
            <NotificationDropdown />
          </div>

          {/* User Profile */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="flex items-center space-x-2 sm:space-x-3 text-gray-700 hover:text-gray-900 transition-colors">
                <div className="p-1 rounded-lg bg-blue-50/50 backdrop-blur-md border border-blue-100/50 hover:bg-blue-100/60 hover:border-blue-200/60 transition-all duration-200">
                  <Avatar.Root className="flex h-7 w-7 select-none items-center justify-center rounded-full bg-blue-100/60">
                    <Avatar.Image
                      className="h-full w-full rounded-[inherit] object-cover"
                      src="/avatars/client1.png"
                      alt="Admin User"
                    />
                    <Avatar.Fallback className="flex h-full w-full items-center justify-center rounded-full bg-blue-200/60">
                      <User className="h-4 w-4 text-blue-500" />
                    </Avatar.Fallback>
                  </Avatar.Root>
                </div>
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900">{session?.user?.name || 'Admin User'}</p>
                  <p className="text-xs text-gray-500">{session?.user?.email || 'admin@makemybrand.com'}</p>
                </div>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-[220px] bg-white rounded-md p-1 shadow-md border border-gray-200"
                sideOffset={5}
              >
                <DropdownMenu.Item className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer">
                  <User className="h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenu.Item>
                <DropdownMenu.Item className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer">
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
                <DropdownMenu.Item
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded cursor-pointer"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </header>
  );
}
